import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, RefreshCw, ArrowLeft, Ban, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCardStatusBadge } from '../utils/cardStatus';
import OTPVerificationModal from './OTPVerificationModal';
import { SESSION_KEY } from '../helper/common';

const config = {
  apiUrl: process.env.REACT_APP_API_URL_User,
  labels: {
    selectCard: 'Select a Card',
    cardholderName: 'Cardholder Name',
    creditCardNumber: 'Credit Card Number',
    status: 'Status',
    cardType: 'Card Type',
    actions: 'Actions',
    primaryCard: 'Primary',
    supplementaryCard: 'Supplementary',
    manage: 'Replace',
  },
};

const CardReplacement = () => {
  const navigate = useNavigate();
  const [cardList, setCardList] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isReplacingCard, setIsReplacingCard] = useState(false);
  const [showAlreadyBlockedPopup, setShowAlreadyBlockedPopup] = useState(false);
  const [currentCardStatus, setCurrentCardStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOTPModal, setShowOTPModal] = useState(false);

  useEffect(() => {
    fetchCardList();
  }, []);

  const fetchCardList = async () => {
    try {
      setLoading(true);
      setError(null);
      const custId = localStorage.getItem('custId');
      if (!custId) {
        throw new Error('Customer ID not found. Please visit the dashboard.');
      }
      const token = localStorage.getItem(SESSION_KEY);

      const response = await axios.get(`${config.apiUrl}/api/user/list`, {
        params: { custId },
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const cardsData = response.data.cards || [];
      setCardList(cardsData);

      const storedCardNumber = localStorage.getItem('cardNumber');
      const storedCardStatus = localStorage.getItem('cardStatus') || 'A';
      if (storedCardNumber && cardsData.length > 0) {
        const card = cardsData.find((c) => c.cardNumber === storedCardNumber);
        if (card) {
          setSelectedCard(card);
          setCurrentCardStatus(card.status || storedCardStatus);
        }
      }

      if (cardsData.length > 0 && !selectedCard) {
        setSelectedCard(cardsData[0]);
        setCurrentCardStatus(cardsData[0].status || 'A');
      }

      const hasActiveCards = cardsData.some((card) => card.status === 'A');
      if (!hasActiveCards && cardsData.length > 0) {
        setShowAlreadyBlockedPopup(true);
      }
    } catch (err) {
      console.error('Error fetching card list:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load card list';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCard && (selectedCard.status === 'T' || selectedCard.status === 'BLOCKED')) {
      const hasActiveCards = cardList.some((card) => card.status === 'A');
      setShowAlreadyBlockedPopup(!hasActiveCards);
    }
  }, [selectedCard, cardList]);

  const executeReplaceCard = async () => {
    if (!selectedCard?.cardNumber) {
      toast.error('Please select a valid card to replace');
      return;
    }
    if (!selectedReason) {
      toast.error('Please select a reason for replacement');
      return;
    }
    if (selectedCard.status !== 'A') {
      toast.error('Cannot replace a card that is not active');
      setShowAlreadyBlockedPopup(true);
      return;
    }
    if (!agreeToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setIsReplacingCard(true);
    try {
      const token = localStorage.getItem(SESSION_KEY);
      if (!token) {
        throw new Error('Authentication token not found');
      }
      const response = await axios.post(
        `${config.apiUrl}/api/cards/replace`,
        {
          cardNumber: selectedCard.cardNumber,
          cardReason: selectedReason === 'Stolen' ? 'S' : 'L',
          cnic: localStorage.getItem('cnic'),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`

          },
        }
      );

      const result = response.data;
      // console.log('API Response:', result);

      if (response.status === 200) {
        if (result.true) {
          toast.success(result.true || 'Replacement request has been submitted successfully!');
          const replacedStatus = selectedReason === 'Stolen' ? 'S' : 'L';
          setCurrentCardStatus(replacedStatus);
          setAgreeToTerms(false);
          setSelectedReason('');
          localStorage.setItem('cardStatus', replacedStatus);
          const updatedCardList = cardList.map((card) =>
            card.cardNumber === selectedCard.cardNumber ? { ...card, status: replacedStatus } : card
          );
          setCardList(updatedCardList);
          setSelectedCard({ ...selectedCard, status: replacedStatus });
          const hasActiveCards = updatedCardList.some((card) => card.status === 'A');
          if (!hasActiveCards) {
            setTimeout(() => setShowAlreadyBlockedPopup(true), 1000);
          }
        } else {
          toast.error(result.false || 'Failed to request card replacement');
        }
      }
    } catch (error) {
      console.error('Network error:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        toast.error(error.response.data?.false || 'Failed to request card replacement');
      } else {
        toast.error('Network error: Unable to connect to the server');
      }
    } finally {
      setIsReplacingCard(false);
    }
  };

  const handleReplaceCard = () => {
    if (!selectedCard?.cardNumber) { toast.error('Please select a valid card to replace'); return; }
    if (!selectedReason) { toast.error('Please select a reason for replacement'); return; }
    if (selectedCard.status !== 'A') { toast.error('Cannot replace a card that is not active'); setShowAlreadyBlockedPopup(true); return; }
    if (!agreeToTerms) { toast.error('Please agree to the terms and conditions'); return; }
    setShowOTPModal(true);
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToCardManagement = () => {
    navigate('/cardManagement');
  };

  const handleClosePopup = () => {
    setShowAlreadyBlockedPopup(false);
    navigate('/block-unblock');
  };

  const handleCardSelect = (e) => {
    const cardId = parseInt(e.target.value);
    const card = cardList.find((c) => c.id === cardId);
    if (card) {
      setSelectedCard(card);
      setCurrentCardStatus(card.status);
      localStorage.setItem('cardNumber', card.cardNumber);
      localStorage.setItem('cardStatus', card.status);
      const hasActiveCards = cardList.some((c) => card.status === 'A');
      setShowAlreadyBlockedPopup(!hasActiveCards && (card.status === 'T' || card.status === 'BLOCKED'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading card list...</div>
      </div>
    );
  }

  if (error || !localStorage.getItem('custId')) {
    return (
      <section className="min-h-screen py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl p-8 max-w-md mx-auto">
              <CreditCard className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">No Card Found</h3>
              <p className="text-red-700 dark:text-red-300 mb-4">
                {error || 'Please visit the dashboard first to load your card information.'}
              </p>
              <button
                onClick={handleGoToDashboard}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <OTPVerificationModal
        isOpen={showOTPModal}
        actionLabel="Request Replacement"
        onSuccess={() => {
          setShowOTPModal(false);
          executeReplaceCard();
        }}
        onCancel={() => setShowOTPModal(false)}
      />
      {showAlreadyBlockedPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50 mb-4">
                <Ban className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {currentCardStatus === 'T' ? 'No Active Cards Left' : 'All Cards Blocked'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {currentCardStatus === 'T'
                  ? 'All your cards are currently blocked. Please unblock a card to continue.'
                  : 'All cards are blocked and cannot be replaced.'}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleClosePopup}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Go to Block/Unblock
                </button>
                {/* <button
                  onClick={() => navigate('/unBlocks')}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Unblock Card
                </button> */}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {!showAlreadyBlockedPopup && (
        <section className="min-h-screen py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="flex justify-center space-x-4 mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </button>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 dark:border-gray-700/50">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-blue-100 dark:bg-blue-900/50 rounded-full">
                    <RefreshCw className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-400 dark:to-blue-500 bg-clip-text text-transparent">
                  Card Replacement
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">
                  Request a replacement for your card
                </p>
                {cardList.length > 0 && (
                  <>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        {config.labels.selectCard}
                      </label>
                      <select
                        value={selectedCard ? selectedCard.id : ''}
                        onChange={handleCardSelect}
                        className="w-full px-4 py-3 border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                      >
                        <option value="" disabled>
                          Select a card
                        </option>
                        {cardList.map((card) => (
                          <option key={card.id} value={card.id}>
                            {card.cardholderName} (•••• {card.cardNumber.slice(-4)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        Reason for Replacement
                      </label>
                      <select
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                      >
                        <option value="" disabled>
                          Select a reason
                        </option>
                        <option value="Stolen">Stolen</option>
                        <option value="Lost">Lost</option>
                      </select>
                    </div>
                  </>
                )}
                {selectedCard && (
                  // <div className="flex justify-center items-center space-x-2 mb-4">
                  //   <p className="text-slate-500 dark:text-slate-500 text-sm">
                  //     Customer Card: •••• {selectedCard.cardNumber.slice(-4)}
                  //   </p>
                  //   <span
                  //     className={`px-2 py-1 rounded-full text-xs font-medium ${
                  //       selectedCard.status === 'A'
                  //         ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                  //         : selectedCard.status === 'R'
                  //         ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                  //         : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                  //     }`}
                  //   >
                  //     {getStatusBadge(selectedCard.status).label}
                  //   </span>
                  // </div>


                  <div className="flex justify-center items-center space-x-2 mb-4">
                    <p className="text-slate-500 dark:text-slate-500 text-sm">
                      Customer Card: •••• {selectedCard.cardNumber.slice(-4)}
                    </p>
                    {(() => {
                      const statusBadge = getCardStatusBadge(selectedCard.status);
                      const StatusIcon = statusBadge.icon;

                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center ${statusBadge.classes}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusBadge.label}
                        </span>
                      );
                    })()}
                  </div>

                )}
                <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-xl p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">Important Note</h3>
                  </div>
                  <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-2 list-disc list-inside">
                    <li>Your card will be deactivated and a replacement will be issued.</li>
                    <li>Replacement may take a few business days to process.</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {cardList.length > 0 && (
              <div className="mb-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="h-5 w-5 text-blue-600"
                  />
                  <span className="text-slate-600 dark:text-slate-400">
                    I agree to the terms and conditions
                  </span>
                </label>
              </div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="text-center space-y-4"
            >
              <button
                onClick={handleReplaceCard}
                disabled={!agreeToTerms || isReplacingCard || !selectedCard || !selectedReason || currentCardStatus !== 'A'}
                className={`flex items-center space-x-3 mx-auto px-8 py-4 rounded-xl text-lg font-semibold transition-all ${agreeToTerms && selectedCard && selectedReason && currentCardStatus === 'A'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                  : 'bg-slate-300 dark:bg-gray-600 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                  } ${isReplacingCard ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {currentCardStatus === 'T' || currentCardStatus === 'B' ? (
                  <>
                    <Ban className="h-5 w-5" />
                    <span>Card Already Blocked</span>
                  </>
                ) : currentCardStatus === 'R' ? (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    <span>Card Already Replaced</span>
                  </>
                ) : isReplacingCard ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Requesting Replacement...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    <span>Request Card Replacement</span>
                  </>
                )}
              </button>
              <button
                onClick={handleGoToCardManagement}
                className="flex items-center space-x-2 mx-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CreditCard className="h-4 w-4" />
                <span>Card Settings</span>
              </button>
              {(currentCardStatus === 'T' || currentCardStatus === 'B') && selectedCard && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-2">
                  Your card is already blocked. Please unblock it first.
                </p>
              )}
              {currentCardStatus === 'R' && selectedCard && (
                <p className="text-blue-600 dark:text-blue-400 text-sm mt-2">
                  A replacement for this card has already been requested.
                </p>
              )}
            </motion.div>
          </div>
        </section>
      )}
    </>
  );
};

export default CardReplacement;